'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { SITE_CONFIG } from '../../constants';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  /** What the user picked: 'system' follows OS preference. */
  preference: ThemePreference;
  /** What is actually applied to the DOM right now. */
  resolvedTheme: ResolvedTheme;
  setPreference: (pref: ThemePreference) => void;
  /** Cycle: light → dark → system → light. */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const COOKIE_NAME = 'theme';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year
const DARK_MQ = '(prefers-color-scheme: dark)';

function readStoredPreference(): ThemePreference {
  if (typeof document === 'undefined') return SITE_CONFIG.defaultTheme;
  try {
    const m = document.cookie.match(/(?:^|;\s*)theme=([^;]+)/);
    if (m) {
      const v = decodeURIComponent(m[1]);
      if (v === 'light' || v === 'dark' || v === 'system') return v;
    }
  } catch {
    /* ignore */
  }
  return SITE_CONFIG.defaultTheme;
}

function writeStoredPreference(pref: ThemePreference) {
  if (typeof document === 'undefined') return;
  try {
    document.cookie = `${COOKIE_NAME}=${pref}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia(DARK_MQ).matches ? 'dark' : 'light';
}

function resolve(pref: ThemePreference): ResolvedTheme {
  return pref === 'system' ? getSystemTheme() : pref;
}

function applyToDom(theme: ResolvedTheme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // SSR-safe defaults. The inline <head> bootstrap script has already set
  // data-theme on <html> before this component renders, so the visual state
  // is already correct - these initial values just feed React's tree.
  const [preference, setPreferenceState] = useState<ThemePreference>(SITE_CONFIG.defaultTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(
    SITE_CONFIG.defaultTheme === 'dark' ? 'dark' : 'light',
  );

  // Hydrate from localStorage on mount.
  useEffect(() => {
    const pref = readStoredPreference();
    setPreferenceState(pref);
    setResolvedTheme(resolve(pref));
  }, []);

  // Apply data-theme on changes. Skip the very first run so we don't clobber
  // the value the bootstrap script already set before any paint.
  const skipFirstApply = useRef(true);
  useEffect(() => {
    if (skipFirstApply.current) {
      skipFirstApply.current = false;
      return;
    }
    applyToDom(resolvedTheme);
  }, [resolvedTheme]);

  // While preference is 'system', track OS-level dark-mode changes live.
  useEffect(() => {
    if (preference !== 'system') return;
    const mql = window.matchMedia(DARK_MQ);
    const onChange = () => setResolvedTheme(mql.matches ? 'dark' : 'light');
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback((pref: ThemePreference) => {
    writeStoredPreference(pref);
    setPreferenceState(pref);
    setResolvedTheme(resolve(pref));
  }, []);

  const toggle = useCallback(() => {
    setPreferenceState((current) => {
      const next: ThemePreference =
        current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
      writeStoredPreference(next);
      setResolvedTheme(resolve(next));
      return next;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, resolvedTheme, setPreference, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be called inside <ThemeProvider>');
  }
  return ctx;
}
